// Slim types port. ASCII / pattern overlay types from oq-images are dropped.
import type { SceneKind } from "../data/sceneKinds";
import type { ThemeMode } from "./sceneAssets";

export type BrandMarkChoice = "icon" | "logo";
export type BrandSideChoice = "front" | "back" | "left" | "right" | "top";
export type BrandCanvasPose = "upright" | "flat";
export type BrandTextFont = "sans" | "mono";
export type BrandMaterialMode = "neutral" | "accent" | "white";

// rack-row composite presets. Each preset names a cabinet count + whether
// to draw end caps and overhead trays. "custom" uses the explicit
// `rowCount` field. See `lib/rackRow.ts` for the resolved geometry.
export type RackRowPreset =
  | "single"
  | "short"
  | "medium"
  | "long"
  | "hall"
  | "custom";

export type SceneObject = {
  id: string;
  kind: SceneKind;
  x: number;
  z: number;
  scale: number;
  rotation: number; // radians
  accent?: boolean;       // true = blue brand override
  power?: boolean;
  /** Which OneQode mark to stamp on this object (none if undefined). */
  brandMark?: BrandMarkChoice | null;
  /** Which face the mark sits on. Ignored when `brandMark` is null/undefined. */
  brandSide?: BrandSideChoice | null;
  /** How thick the brand mark protrudes from the wall, as a multiplier on
   *  the master's authored 0.32m thickness. 1.0 = default; valid 0.2..3.0. */
  brandThickness?: number;
  /** Rotate the placed brand mark 180 degrees within the chosen face. */
  brandFlip?: boolean;
  /** Mirror the placed brand mark horizontally on its face. Useful when
   *  a side's basis ends up showing the glyph reversed (e.g. the "right"
   *  face mirrors against the "left" face). Independent of brandFlip
   *  (which is a 180° in-plane rotation). */
  brandInvert?: boolean;
  /** First-class Brand objects can stand upright or lay flat on the canvas. */
  brandPose?: BrandCanvasPose;
  /** Runtime 3D text content for `text-label` objects. */
  textContent?: string;
  /** Runtime 3D text font choice. */
  textFont?: BrandTextFont;
  /** Runtime 3D text extrusion depth in world units. */
  textDepth?: number;
  /** Material used by standalone brand/text objects and attached brand decals. */
  brandColor?: BrandMaterialMode;
  // ---- rack-row composite controls (only meaningful when kind === "rack-row")
  /** Preset shape of the row. "custom" uses `rowCount` directly. */
  rowPreset?: RackRowPreset;
  /** Cabinet count when `rowPreset === "custom"`. Clamped 1..64 in lib/rackRow.ts. */
  rowCount?: number;
  /** When true, mirror the row across an aisle (one-click corridor). */
  rowPair?: boolean;
  /** Aisle width in metres when `rowPair === true`. Default ~2.4 m. */
  rowAisleWidth?: number;
};

export type LinkConnection = {
  id: string;
  fromId: string;
  toId: string;
};

// Camera state — Blender-style orbit camera.
// `target` is the world point the camera is looking at.
// `yaw` rotates around world Y, `pitch` is the elevation, `distance` is radius.
export type CameraState = {
  targetX: number;
  targetY: number;
  targetZ: number;
  yaw: number;       // radians; 0 = camera at +X
  pitch: number;     // radians; 0 = horizontal, π/2 = top-down
  distance: number;  // world units
  fov: number;       // degrees
};

export type SceneSettings = {
  groundSize: number;
  camera: CameraState;
  dotGrid: boolean;
  dotGridSpacing: number;
  /** Dot radius as a fraction of the cell pitch (0.005..0.10). 0.015 ≈ default. */
  dotGridSize?: number;
  dotGridOpacityLight: number;
  dotGridOpacityDark: number;
  dotColorLight: string;
  dotColorDark: string;
  animateLinks: boolean;
  linkSpeed: number;
  linkThickness: number;
  linkColorLight: string;
  linkColorDark: string;
  straightLinks: boolean;
  // Cable opacity 0..1 (the steady tube colour).
  linkCableOpacity: number;
  // Power-strip opacity 0..1 (the flowing pulse on top of the cable).
  linkStripOpacity: number;
  // When false, the flow animation stops and the strip fades to its base.
  linkPower: boolean;
  accentLight: string;
  accentDark: string;
  theme: ThemeMode;
  floatEnabled: boolean;
  floatHeight: number;
  floatShadowSize: number;
  floatShadowOpacity: number;
  // ---- Guides (draggable on-floor reference lines, like Figma rulers in 3D)
  /** Master toggle for the guide-line overlay. Default false (off). */
  gridLines?: boolean;
  /** When true, draw a small `+` at every X×Z guide intersection. */
  gridLinesShowCrosses?: boolean;
  /** Cross arm length in metres. */
  gridLinesCrossSize?: number;
  /** Per-theme line opacity 0..1. */
  gridLinesOpacityLight?: number;
  gridLinesOpacityDark?: number;
  /** Per-theme cross opacity 0..1. Independent of line opacity so the
   *  intersections can be emphasised or de-emphasised on their own. */
  gridLinesCrossOpacityLight?: number;
  gridLinesCrossOpacityDark?: number;
  /** Per-theme stroke colour (hex). Empty string = auto from palette. */
  gridLinesColorLight?: string;
  gridLinesColorDark?: string;
  /** Length of each dash segment in metres. Small values + larger gaps
   *  read as a stippled "dotted" line. */
  gridLinesDashSize?: number;
  /** Length of the gap between dashes in metres. */
  gridLinesGapSize?: number;
  /** Persistent guide list. Each guide is one line, constrained to slide on
   *  its perpendicular axis (axis="x" runs along world-X, slides on Z;
   *  axis="z" runs along world-Z, slides on X). */
  guides?: Guide[];
};

/** One draggable guide line. */
export type Guide = {
  id: string;
  /** "x" — line runs along world X (the Z value moves it).
   *  "z" — line runs along world Z (the X value moves it). */
  axis: "x" | "z";
  /** The line's offset along its perpendicular axis, in world units. */
  coord: number;
};

export function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

// Canonical camera presets. pitch is clamped >= 0.05 elsewhere so the camera
// never dips below the floor.
//
// World axes in this app: +X right, +Y up, +Z toward viewer (Three.js default).
// "yaw" is the angle around world Y; yaw=0 puts the camera on +X looking at
// the origin, yaw=π/2 puts it on +Z. The four iso presets place the camera
// in the four diagonal quadrants at a fixed iso pitch (~30°).
//
// NE = camera in +X/+Z quadrant, looking toward origin (so the user sees the
// scene from the north-east). The other three rotate yaw by π/2 increments.
export const ISO_PITCH = Math.PI / 5; // ~36° elevation; classic iso feel
export const CAMERA_VIEWS: Record<string, Partial<CameraState>> = {
  // ----- Six axial views (Numpad mapping in parens) -----
  // Numpad 1 — front (looking down -Z)
  front: { yaw: -Math.PI / 2, pitch: 0.05 },
  // Ctrl-Numpad 1 — back (looking down +Z)
  back:  { yaw:  Math.PI / 2, pitch: 0.05 },
  // Numpad 3 — right (looking down -X)
  right: { yaw: 0,            pitch: 0.05 },
  // Ctrl-Numpad 3 — left (looking down +X)
  left:  { yaw: Math.PI,      pitch: 0.05 },
  // Numpad 7 — top (looking down -Y)
  top:    { yaw: -Math.PI / 2, pitch: Math.PI / 2 - 0.12 },
  // Ctrl-Numpad 7 — bottom (looking up +Y) — clamped just above the floor
  // so the camera doesn't dip below the ground plane.
  bottom: { yaw: -Math.PI / 2, pitch: 0.05 },
  // ----- Iso quadrants -----
  iso_ne: { yaw: Math.PI / 4,           pitch: ISO_PITCH },
  iso_nw: { yaw: 3 * Math.PI / 4,       pitch: ISO_PITCH },
  iso_sw: { yaw: -3 * Math.PI / 4,      pitch: ISO_PITCH },
  iso_se: { yaw: -Math.PI / 4,          pitch: ISO_PITCH },
  // Backwards-compat alias used by older code paths.
  iso:    { yaw: Math.PI / 4,           pitch: ISO_PITCH },
};
